import { useMutation, useQuery, useSuspenseQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Heading,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTooltip,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { TenantSelector } from '@eluve/smart-blocks';

export const getTemplateBundleDefaultModelsQuery = graphql(`
  query getTemplateBundleDefaultModels {
    templateBundleDefaultModels {
      __typename
      id
      tenantId
      name
      description
      rank
      modelArgsId
      model_args {
        __typename
        id
        name
        modelType
      }
      tenant {
        __typename
        id
        name
      }
    }
  }
`);

const getActiveModelArgsForBundleDefaultsQuery = graphql(`
  query getActiveModelArgsForBundleDefaults {
    llmModelArgs(where: { isActive: { _eq: true } }) {
      __typename
      id
      name
      modelType
    }
  }
`);

const insertTemplateBundleDefaultModelMutation = graphql(`
  mutation insertTemplateBundleDefaultModel(
    $tenantId: uuid
    $name: String!
    $description: String
    $rank: Int!
    $modelArgsId: uuid!
  ) {
    insertTemplateBundleDefaultModelsOne(
      object: {
        tenantId: $tenantId
        name: $name
        description: $description
        rank: $rank
        modelArgsId: $modelArgsId
      }
    ) {
      __typename
      id
    }
  }
`);

const updateTemplateBundleDefaultModelMutation = graphql(`
  mutation updateTemplateBundleDefaultModel(
    $id: uuid!
    $tenantId: uuid
    $name: String!
    $description: String
    $rank: Int!
    $modelArgsId: uuid!
  ) {
    updateTemplateBundleDefaultModelsByPk(
      pkColumns: { id: $id }
      _set: {
        tenantId: $tenantId
        name: $name
        description: $description
        rank: $rank
        modelArgsId: $modelArgsId
      }
    ) {
      __typename
      id
    }
  }
`);

const deleteTemplateBundleDefaultModelMutation = graphql(`
  mutation deleteTemplateBundleDefaultModel($id: uuid!) {
    deleteTemplateBundleDefaultModelsByPk(id: $id) {
      __typename
      id
    }
  }
`);

const TemplateBundleDefaultModelSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  rank: z.coerce.number().int(),
  modelArgsId: z.string().min(1, 'Select a model'),
});

type TemplateBundleDefaultModelForm = z.infer<
  typeof TemplateBundleDefaultModelSchema
>;

type TemplateBundleDefaultModelRow = {
  id: string;
  tenantId: string | null;
  name: string;
  description: string | null;
  rank: number;
  modelArgsId: string;
  modelLabel: string;
  scope: string;
};

type SelectedTenant = { id: string; name: string } | null;

// Private Components

interface TemplateBundleDefaultModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this row; otherwise it creates a new one */
  row?: TemplateBundleDefaultModelRow | null;
}

const TemplateBundleDefaultModelDialog: React.FC<
  TemplateBundleDefaultModelDialogProps
> = ({ open, onOpenChange, row }) => {
  const toast = useToast();
  const isEditing = Boolean(row);

  const { data: modelArgsData } = useQuery(
    getActiveModelArgsForBundleDefaultsQuery,
  );

  const [selectedTenant, setSelectedTenant] = useState<SelectedTenant>(null);

  const form = useForm<TemplateBundleDefaultModelForm>({
    resolver: zodResolver(TemplateBundleDefaultModelSchema),
    mode: 'onChange',
    values: {
      name: row?.name ?? '',
      description: row?.description ?? '',
      rank: row?.rank ?? 0,
      modelArgsId: row?.modelArgsId ?? '',
    },
  });

  const mutationOptions = {
    refetchQueries: [{ query: getTemplateBundleDefaultModelsQuery }],
    onCompleted: () => {
      toast.success(
        isEditing ? 'Updated default model' : 'Added default model',
      );
      form.reset();
      setSelectedTenant(null);
      onOpenChange(false);
    },
    onError: () =>
      toast.error(
        isEditing
          ? 'Failed to update default model'
          : 'Failed to add default model',
      ),
  };

  const [insertDefaultModel] = useMutation(
    insertTemplateBundleDefaultModelMutation,
    mutationOptions,
  );
  const [updateDefaultModel] = useMutation(
    updateTemplateBundleDefaultModelMutation,
    mutationOptions,
  );

  const onSubmit = async (data: TemplateBundleDefaultModelForm) => {
    const variables = {
      tenantId: selectedTenant?.id ?? row?.tenantId ?? null,
      name: data.name,
      description: data.description || null,
      rank: data.rank,
      modelArgsId: data.modelArgsId,
    };

    if (row) {
      await updateDefaultModel({ variables: { id: row.id, ...variables } });
    } else {
      await insertDefaultModel({ variables });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className="flex max-h-[80vh] flex-col gap-5 overflow-y-auto p-8"
      >
        <Heading size="3xl">
          {isEditing ? 'Edit Default Model' : 'Add Default Model'}
        </Heading>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-lg">
                  <Label>Public Name</Label>
                  <Input
                    className="bg-white/5"
                    placeholder="e.g. Fast, Thinking"
                    {...field}
                  />
                  {error && <FormMessage>{error.message}</FormMessage>}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-lg">
                  <Label>Description (internal)</Label>
                  <Textarea
                    className="bg-white/5"
                    placeholder="Description"
                    {...field}
                  />
                  {error && <FormMessage>{error.message}</FormMessage>}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rank"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-lg">
                  <Label>Rank</Label>
                  <Input
                    className="bg-white/5"
                    type="number"
                    placeholder="0"
                    {...field}
                  />
                  {error && <FormMessage>{error.message}</FormMessage>}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="modelArgsId"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-lg">
                  <Label>Model Args</Label>
                  <Select
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a configured model" />
                    </SelectTrigger>
                    <SelectContent>
                      {modelArgsData?.llmModelArgs.map((modelArgs) => (
                        <SelectItem key={modelArgs.id} value={modelArgs.id}>
                          {modelArgs.name
                            ? `${modelArgs.name} (${modelArgs.modelType})`
                            : modelArgs.modelType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {error && <FormMessage>{error.message}</FormMessage>}
                </FormItem>
              )}
            />
            <div className="max-w-lg space-y-2">
              <Label>Tenant (leave empty for a global preset)</Label>
              <Stack direction="horizontal" gap={2} align="center">
                <TenantSelector
                  selectedTenant={selectedTenant}
                  onTenantSelected={setSelectedTenant}
                  placeholder={
                    row?.tenantId ? row.scope : 'Global (all tenants)'
                  }
                />
                {selectedTenant && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedTenant(null)}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </div>

            <Button
              type="submit"
              className="w-fit"
              disabled={!form.formState.isValid}
            >
              {isEditing ? 'Save' : 'Add'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const DeleteTemplateBundleDefaultModelAction: React.FC<{
  row: TemplateBundleDefaultModelRow;
}> = ({ row }) => {
  const toast = useToast();

  const [deleteDefaultModel] = useMutation(
    deleteTemplateBundleDefaultModelMutation,
    {
      variables: { id: row.id },
      refetchQueries: [{ query: getTemplateBundleDefaultModelsQuery }],
      onCompleted: () => toast.success('Deleted default model'),
      onError: () => toast.error('Failed to delete default model'),
    },
  );

  return (
    <AlertDialog>
      <SimpleTooltip label={`Delete ${row.name}`}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" aria-label={`Delete ${row.name}`}>
            <Icon name="Trash2" size="xl" />
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete "{row.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the default model preset. Users will no longer see it
            as an option. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => deleteDefaultModel()}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Public Components

/**
 * Admin section for configuring the default model presets available to
 * template bundles: ranked, publicly-named entries (e.g. Fast, Thinking)
 * each backed by a configured llm_model_args row. Presets are global
 * (no tenant) or tenant-specific.
 */
export const TemplateBundleDefaultModelsSection: React.FC = () => {
  const {
    data: { templateBundleDefaultModels },
  } = useSuspenseQuery(getTemplateBundleDefaultModelsQuery);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] =
    useState<TemplateBundleDefaultModelRow | null>(null);

  const rows = useMemo(() => {
    return templateBundleDefaultModels.map<TemplateBundleDefaultModelRow>(
      (defaultModel) => ({
        id: defaultModel.id,
        tenantId: defaultModel.tenantId,
        name: defaultModel.name,
        description: defaultModel.description,
        rank: defaultModel.rank,
        modelArgsId: defaultModel.modelArgsId,
        modelLabel: defaultModel.model_args.name
          ? `${defaultModel.model_args.name} (${defaultModel.model_args.modelType})`
          : defaultModel.model_args.modelType,
        scope: defaultModel.tenant?.name ?? 'Global',
      }),
    );
  }, [templateBundleDefaultModels]);

  const columns = useMemo(
    () =>
      new ColDefBuilder<TemplateBundleDefaultModelRow>()
        // Custom renderer: defaultSortable renders falsy values (rank 0) as "—"
        .defaultSortable('rank', {
          cellRenderer: (row) => <Text>{row.rank}</Text>,
        })
        .defaultSortable('name')
        .defaultSortable('description', {
          cellRenderer: (row) =>
            row.description ? (
              <div className="max-w-md truncate" title={row.description}>
                {row.description}
              </div>
            ) : (
              <Text color="disabled">—</Text>
            ),
        })
        .defaultSortable('modelLabel', 'Model')
        .defaultSortable('scope')
        .colDef({
          header: 'Actions',
          cell: ({ row: { original } }) => (
            <Stack direction="horizontal" fullWidth={false} gap={1}>
              <SimpleTooltip label={`Edit ${original.name}`}>
                <Button
                  variant="outline"
                  aria-label={`Edit ${original.name}`}
                  onClick={() => {
                    setEditingRow(original);
                    setDialogOpen(true);
                  }}
                >
                  <Icon name="Pencil" size="xl" />
                </Button>
              </SimpleTooltip>
              <DeleteTemplateBundleDefaultModelAction row={original} />
            </Stack>
          ),
        })
        .build(),
    [],
  );

  return (
    <>
      <Stack
        direction="horizontal"
        align="center"
        justify="between"
        className="mb-3"
      >
        <div className="my-2 py-2">
          <Heading size="xl">Template Bundle Default Models</Heading>
          <Text color="tertiary">
            Ranked model presets (e.g. Fast, Thinking) shown to users, each
            backed by a configured model args entry. Global presets apply to all
            tenants; tenant presets only to that tenant.
          </Text>
        </div>
        <Button
          onClick={() => {
            setEditingRow(null);
            setDialogOpen(true);
          }}
        >
          <Icon name="Plus" size="lg" className="mr-2" />
          Add default model
        </Button>
      </Stack>
      <DataTable
        columns={columns}
        data={rows}
        enableGlobalSearch
        initialSortingState={[{ id: 'rank', desc: false }]}
      />
      <TemplateBundleDefaultModelDialog
        // Remount the dialog per target row so form values reset cleanly
        key={editingRow?.id ?? 'create'}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        row={editingRow}
      />
    </>
  );
};
